import { Cuenta, Transaccion } from '../types';

/**
 * Apuntar un gasto desde fuera de la app: los atajos de myarinn en Atajos,
 * Siri y Spotlight (ios/App/App/AtajosMyarinn.swift), la automatización de
 * Wallet al pagar con Apple Pay o un enlace abren
 *
 *   myarinn://apuntar?importe=12,40&comercio=MERCADONA&tarjeta=Visa
 *   https://myarinn.app/apuntar?importe=12,40&nota=Café
 *
 * y la app abre el formulario de gasto rellenado. Además se propone la
 * categoría, la cuenta y el presupuesto del último gasto en ese comercio, y la
 * cuenta de cada tarjeta de Wallet (`Cuenta.tarjetasApplePay`, en Plus).
 */

export interface DatosApuntar {
  tipo: 'gasto' | 'ingreso';
  /** Listo para el campo de importe («12,40»); vacío si no venía o no se entiende. */
  importe: string;
  descripcion: string;
  /** Nombre de la tarjeta de Wallet, para recordar su cuenta. */
  tarjeta: string;
  /** «AAAA-MM-DD», si venía y es válida. */
  fecha: string;
}

const MAX_TEXTO = 80;

const limpiar = (t: string | null) => (t ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_TEXTO);

/**
 * Lo que da Wallet o escribe alguien en un Atajo: «12,40 €», «€12.40»,
 * «1.234,50», «-3,5». Siempre en positivo y con coma; vacío si no es un importe.
 */
export function importeDeTexto(texto: string | null): string {
  let t = (texto ?? '').replace(/[^\d.,]/g, '');
  if (!t) return '';
  const coma = t.lastIndexOf(','), punto = t.lastIndexOf('.');
  if (coma >= 0 && punto >= 0) {
    // Con los dos, el último es el decimal y el otro separa miles
    const decimal = coma > punto ? ',' : '.';
    t = t.split(decimal === ',' ? '.' : ',').join('').replace(decimal, '.');
  } else if (punto >= 0 && /^\d{1,3}(\.\d{3})+$/.test(t)) {
    t = t.split('.').join('');          // «1.234»: miles
  } else {
    t = t.replace(',', '.');
  }
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return '';
  const redondeado = Math.round(n * 100) / 100;
  return (Number.isInteger(redondeado) ? String(redondeado) : redondeado.toFixed(2)).replace('.', ',');
}

export function leerEnlaceApuntar(search: string): DatosApuntar {
  const p = new URLSearchParams(search);
  const fecha = p.get('fecha') ?? '';
  return {
    tipo: p.get('tipo') === 'ingreso' ? 'ingreso' : 'gasto',
    importe: importeDeTexto(p.get('importe') ?? p.get('cantidad')),
    // Lo que escribe la persona manda sobre el nombre del comercio de Wallet
    descripcion: limpiar(p.get('nota')) || limpiar(p.get('comercio')),
    tarjeta: limpiar(p.get('tarjeta')),
    fecha: /^\d{4}-\d{2}-\d{2}$/.test(fecha) && !isNaN(new Date(fecha).getTime()) ? fecha : '',
  };
}

// Palabras que no distinguen un comercio de otro: «MERCADONA S.A.» es Mercadona
const RUIDO = new Set(['sa', 'sl', 'slu', 'sau', 'sll', 'scoop', 'cb', 'inc', 'ltd', 'es', 'esp']);

/** «MERCADONA, S.A. 1234» y «Mercadona» dan lo mismo: «mercadona». */
export function claveComercio(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\b([a-z])\.([a-z])\.?/g, '$1$2')   // «s.a.» → «sa», «s.l.» → «sl»
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(p => p && !RUIDO.has(p) && !/^\d+$/.test(p))
    .join(' ');
}

const mismoComercio = (a: string, b: string) =>
  !!a && !!b && (a === b || a.startsWith(b + ' ') || b.startsWith(a + ' '));

export interface Sugerencia {
  categoriaId: string;
  cuentaId: string;
  presupuestoId?: string;
}

/**
 * La categoría, la cuenta y el presupuesto del último movimiento de ese tipo
 * con el mismo comercio. Se saltan los adelantos, los traspasos y lo que viene
 * de A medias, que no dicen nada de cómo se apunta ese comercio a mano.
 */
export function sugerenciaPorComercio(
  descripcion: string, tipo: 'gasto' | 'ingreso', transacciones: Transaccion[],
): Sugerencia | null {
  const clave = claveComercio(descripcion);
  if (!clave) return null;
  let mejor: Transaccion | null = null;
  for (const t of transacciones) {
    if (t.tipo !== tipo || t.adelanto || t.traspasoId || t.planId) continue;
    if (!mismoComercio(clave, claveComercio(t.descripcion || ''))) continue;
    if (!mejor || new Date(t.fecha).getTime() > new Date(mejor.fecha).getTime()
        || (new Date(t.fecha).getTime() === new Date(mejor.fecha).getTime() && (t.secuencia ?? 0) > (mejor.secuencia ?? 0))) {
      mejor = t;
    }
  }
  if (!mejor) return null;
  return { categoriaId: mejor.categoriaId, cuentaId: mejor.cuentaId, presupuestoId: mejor.presupuestoId };
}

// ── Tarjeta de Wallet → cuenta ────────────────────────────────────────────
// Cada cuenta guarda los nombres de sus tarjetas tal como los da Wallet
// («Visa BBVA»), en minúsculas. Va en la cuenta, así que vale en cualquier
// dispositivo y se ve y se cambia al editarla.

export const claveTarjeta = (tarjeta: string) => tarjeta.replace(/\s+/g, ' ').trim().toLowerCase();

/** La cuenta que ya tiene asociada esa tarjeta, si alguna. */
export function cuentaDeTarjeta(tarjeta: string, cuentas: Cuenta[]): Cuenta | undefined {
  const clave = claveTarjeta(tarjeta);
  return clave ? cuentas.find(c => c.tarjetasApplePay?.includes(clave)) : undefined;
}

// Lo que dice qué tipo de tarjeta es, no de qué banco
const GENERICAS = new Set(['visa', 'mastercard', 'master', 'card', 'tarjeta', 'debito', 'credito', 'debit',
  'credit', 'classic', 'gold', 'platinum', 'oro', 'cuenta', 'de', 'del', 'la', 'el', 'mi', 'apple', 'pay']);

const palabras = (t: string) => claveComercio(t).split(' ').filter(p => p.length >= 3 && !GENERICAS.has(p));

/**
 * Para la primera vez: la cuenta cuyo nombre comparte alguna palabra con la
 * tarjeta («Visa BBVA» → «BBVA nómina»). Solo si hay una sola que encaje.
 */
export function cuentaParecida(tarjeta: string, cuentas: Cuenta[]): Cuenta | undefined {
  const deTarjeta = new Set(palabras(tarjeta));
  if (deTarjeta.size === 0) return undefined;
  const encajan = cuentas.filter(c => palabras(c.nombre).some(p => deTarjeta.has(p)));
  return encajan.length === 1 ? encajan[0] : undefined;
}

/**
 * Los cambios para dejar la tarjeta solo en `cuentaId`: añadirla ahí y
 * quitarla de cualquier otra que la tuviera. Vacío si ya está así.
 */
export function cambiosTarjeta(tarjeta: string, cuentaId: string, cuentas: Cuenta[]): { id: string; tarjetasApplePay: string[] }[] {
  const clave = claveTarjeta(tarjeta);
  if (!clave || !cuentaId) return [];
  const cambios: { id: string; tarjetasApplePay: string[] }[] = [];
  for (const c of cuentas) {
    const tiene = c.tarjetasApplePay?.includes(clave) ?? false;
    if (c.id === cuentaId && !tiene) cambios.push({ id: c.id, tarjetasApplePay: [...(c.tarjetasApplePay ?? []), clave] });
    if (c.id !== cuentaId && tiene) cambios.push({ id: c.id, tarjetasApplePay: c.tarjetasApplePay!.filter(t => t !== clave) });
  }
  return cambios;
}
